import { useState } from 'react';
import { toast } from 'sonner';
import { ShoppingCart, Paperclip, X, Spinner, FilePdf, Info } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { shizaiApi } from './shizaiApi';

export const PROCUREMENT_META = {
  undecided: {
    label: '未定', chip: 'bg-zinc-100 text-zinc-500 border-dashed border-zinc-300',
    dot: 'bg-zinc-400',
    desc: '調達方法がまだ決まっていない状態。「発注済」にする前に必ずいずれかを選択してください。',
  },
  techs: {
    label: 'TECHS', chip: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    dot: 'bg-emerald-500',
    desc: '基幹システム TECHS で受注登録して手配する部品（受注Noで管理）。',
  },
  direct: {
    label: '直接購入', chip: 'bg-sky-50 text-sky-700 border-sky-200',
    dot: 'bg-sky-500',
    desc: 'Amazon / MonotaRO / Digi-Key などから直接購入（注文番号・請求書を記録）。',
  },
  license: {
    label: 'ライセンス', chip: 'bg-violet-50 text-violet-700 border-violet-200',
    dot: 'bg-violet-500',
    desc: 'ソフトウェア等のライセンス契約。契約期間・更新日・数を管理し、入荷時にライセンス資産へ自動登録。',
  },
  rental: {
    label: 'レンタル', chip: 'bg-amber-50 text-amber-700 border-amber-200',
    dot: 'bg-amber-500',
    desc: 'レンタル・リース契約の機材（契約期間・更新日を管理）。',
  },
};

const DIRECT_VENDORS = ['Amazon', 'MonotaRO', 'Digi-Key', 'Mouser', '楽天'];
const LICENSE_VENDORS = ['Microsoft 365', 'ChatGPT', 'Power BI', 'TECHS', 'CAD'];

/** 調達方法 — PIC が依頼ごとに設定（TECHS / 直接購入 / ライセンス / レンタル）。 */
export default function ProcurementDialog({ req, onClose, onSaved }) {
  const p = req.procurement || {};
  const [method, setMethod] = useState(p.method || 'undecided');
  const [orderVendor, setOrderVendor] = useState(p.order_vendor || '');
  const [orderNo, setOrderNo] = useState(p.order_no || '');
  const [invoiceNo, setInvoiceNo] = useState(p.invoice_no || '');
  const [contractPeriod, setContractPeriod] = useState(p.contract_period || '');
  const [renewalDate, setRenewalDate] = useState(p.renewal_date || '');
  const [seatCount, setSeatCount] = useState(p.seat_count != null ? String(p.seat_count) : '');
  const [attachment, setAttachment] = useState(
    p.invoice_photo_url ? { photo_url: p.invoice_photo_url, filename: p.invoice_photo_filename || '請求書', is_pdf: (p.invoice_photo_filename || '').toLowerCase().endsWith('.pdf') } : null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const uploadInvoice = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      setAttachment(await shizaiApi.uploadPicInvoice(file));
    } catch (e) {
      toast.error(e?.response?.data?.detail || '請求書のアップロードに失敗しました');
    } finally { setUploading(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await shizaiApi.patchProcurement(req.id, {
        method,
        order_vendor: orderVendor.trim() || null,
        order_no: orderNo.trim() || null,
        invoice_no: invoiceNo.trim() || null,
        contract_period: contractPeriod.trim() || null,
        renewal_date: renewalDate || null,
        seat_count: seatCount !== '' ? Number(seatCount) : null,
        invoice_photo_url: attachment?.photo_url || null,
        invoice_photo_filename: attachment?.filename || null,
      });
      toast.success(`調達方法を「${res.method_label}」に設定しました`);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '調達方法の保存に失敗しました');
    } finally { setSaving(false); }
  };

  const vendorField = (label, list, testId) => (
    <div>
      <Label className="text-xs font-semibold text-zinc-600">{label}</Label>
      <Input value={orderVendor} onChange={(e) => setOrderVendor(e.target.value)}
        list="proc-vendor-list" maxLength={200} placeholder={`例: ${list[0]}`}
        className="h-9 mt-1" data-testid={testId} />
      <datalist id="proc-vendor-list">
        {list.map((v) => <option key={v} value={v} />)}
      </datalist>
    </div>
  );

  const attachmentField = (required) => (
    <div>
      <Label className="text-xs font-semibold text-zinc-600">
        請求書添付{required ? <span className="text-rose-500 ml-0.5">＊入荷確認時に必須</span> : '（任意）'}
      </Label>
      {attachment ? (
        <div className="mt-1 inline-flex items-center gap-2 rounded-md border border-zinc-200 bg-zinc-50 px-2.5 py-1.5 text-xs"
          data-testid="proc-attachment-chip">
          {attachment.is_pdf
            ? <FilePdf size={14} weight="duotone" className="text-rose-600" />
            : <Paperclip size={14} className="text-zinc-500" />}
          <span className="max-w-[220px] truncate">{attachment.filename}</span>
          <button type="button" onClick={() => setAttachment(null)}
            className="text-zinc-400 hover:text-rose-600" data-testid="proc-attachment-remove">
            <X size={12} weight="bold" />
          </button>
        </div>
      ) : (
        <label className="mt-1 flex items-center gap-2 rounded-md border border-dashed border-zinc-300 px-3 py-2 text-xs text-zinc-500 cursor-pointer hover:bg-zinc-50 hover:border-sky-300 transition-colors">
          {uploading ? <Spinner size={14} className="animate-spin" /> : <Paperclip size={14} />}
          {uploading ? 'アップロード中...' : '画像 / PDF を選択（12MBまで）'}
          <input type="file" accept="image/*,.pdf,.heic,.heif" className="hidden"
            onChange={(e) => { uploadInvoice(e.target.files?.[0]); e.target.value = ''; }}
            disabled={uploading} data-testid="proc-invoice-file" />
        </label>
      )}
    </div>
  );

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="max-w-md max-h-[88vh] overflow-y-auto" data-testid="procurement-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <ShoppingCart size={20} weight="duotone" className="text-sky-600" />
            調達方法
            <span className="font-mono text-xs text-zinc-500">{req.request_no}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1.5">
            {Object.entries(PROCUREMENT_META).map(([k, m]) => (
              <button key={k} type="button" onClick={() => setMethod(k)}
                data-testid={`proc-method-${k}`}
                className={`w-full flex items-start gap-2.5 rounded-lg border px-3 py-2 text-left transition-colors ${
                  method === k
                    ? 'border-sky-500 bg-sky-50/70 ring-1 ring-sky-500'
                    : 'border-zinc-200 bg-white hover:bg-zinc-50'
                }`}>
                <span className={`mt-1 h-3.5 w-3.5 rounded-full border-2 shrink-0 flex items-center justify-center ${
                  method === k ? 'border-sky-600' : 'border-zinc-300'}`}>
                  {method === k && <span className="h-1.5 w-1.5 rounded-full bg-sky-600" />}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-1.5">
                    <span className={`h-2 w-2 rounded-full ${m.dot}`} />
                    <span className={`text-xs font-bold ${method === k ? 'text-sky-900' : 'text-zinc-800'}`}>{m.label}</span>
                  </span>
                  <span className="block text-[10px] text-zinc-500 mt-0.5 leading-relaxed">{m.desc}</span>
                </span>
              </button>
            ))}
          </div>

          {method === 'techs' && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 px-3 py-2 text-xs text-emerald-800 space-y-1"
              data-testid="proc-techs-info">
              <p className="flex items-center gap-1 font-semibold"><Info size={13} weight="fill" /> TECHS 管理情報</p>
              <p>TECHS受注No: <span className="font-mono font-semibold">{req.techs_order_no || '未入力'}</span></p>
              <p>TECHS登録日: {req.techs_entered_at ? String(req.techs_entered_at).slice(0, 10) : '—'}</p>
              <p>TECHS登録者: {req.techs_entered_by || '—'}</p>
              <p className="text-emerald-700/80">※ TECHS情報はカードの「Techs入力済」チェックから登録・編集できます</p>
            </div>
          )}

          {method === 'direct' && (
            <div className="space-y-3" data-testid="proc-direct-fields">
              {vendorField('注文先', DIRECT_VENDORS, 'proc-order-vendor')}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-semibold text-zinc-600">注文番号</Label>
                  <Input value={orderNo} onChange={(e) => setOrderNo(e.target.value)}
                    maxLength={64} className="h-9 mt-1" data-testid="proc-order-no" />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-zinc-600">請求書番号</Label>
                  <Input value={invoiceNo} onChange={(e) => setInvoiceNo(e.target.value)}
                    maxLength={64} className="h-9 mt-1" data-testid="proc-invoice-no" />
                </div>
              </div>
              {attachmentField(false)}
            </div>
          )}

          {(method === 'license' || method === 'rental') && (
            <div className="space-y-3" data-testid={`proc-${method}-fields`}>
              {vendorField(method === 'license' ? 'ベンダー' : '注文先（ベンダー）',
                method === 'license' ? LICENSE_VENDORS : DIRECT_VENDORS, 'proc-order-vendor')}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-semibold text-zinc-600">契約期間</Label>
                  <Input value={contractPeriod} onChange={(e) => setContractPeriod(e.target.value)}
                    maxLength={120} placeholder="例: 1年 / 2026-04〜2027-03"
                    className="h-9 mt-1" data-testid="proc-contract-period" />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-zinc-600">更新日</Label>
                  <Input type="date" value={renewalDate} onChange={(e) => setRenewalDate(e.target.value)}
                    className="h-9 mt-1" data-testid="proc-renewal-date" />
                </div>
              </div>
              {method === 'license' && (
                <div>
                  <Label className="text-xs font-semibold text-zinc-600">ライセンス数</Label>
                  <Input type="number" min="0" value={seatCount} onChange={(e) => setSeatCount(e.target.value)}
                    className="h-9 mt-1 w-32" data-testid="proc-seat-count" />
                </div>
              )}
              {attachmentField(method === 'license')}
              {method === 'license' && (
                <p className="text-[11px] text-violet-700 bg-violet-50 border border-violet-200 rounded-md px-2.5 py-1.5">
                  入荷確認時に、実際購入金額・ベンダー・更新日・ライセンス数が
                  <span className="font-semibold">ソフトウェアライセンス資産</span>へ自動反映されます
                </p>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={saving} data-testid="proc-cancel">
            キャンセル
          </Button>
          <Button onClick={save} disabled={saving || uploading}
            className="bg-teal-700 hover:bg-teal-800" data-testid="proc-save">
            {saving ? '保存中...' : '保存'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
